
import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Form } from '@/components/ui/form';
import { Button } from '@/components/ui/button';
import { ArrowLeft } from 'lucide-react';
import { useEstimateForm } from './hooks/useEstimateForm';
import { useCustomers } from './hooks/useCustomers';
import { EstimateFormProps, EstimateFormValues } from './types/estimateFormTypes';
import EstimateBasicFields from './components/EstimateBasicFields';
import EstimateSettingsFields from './components/EstimateSettingsFields';
import EstimateAdditionalFields from './components/EstimateAdditionalFields';
import EstimateFormActions from './components/EstimateFormActions';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import EstimateProgress from './components/EstimateProgress';
import PageLayout from '@/components/layout/PageLayout';

const EstimateForm: React.FC<EstimateFormProps> = ({ estimateId }) => {
  const navigate = useNavigate();
  const { form, isLoading, isEditMode, isDataLoaded, handleSubmit } = useEstimateForm(estimateId);
  const { data: customers, isLoading: isCustomersLoading } = useCustomers();
  const { profile } = useAuth();
  const [companyVatStatus, setCompanyVatStatus] = React.useState<{ is_vat_registered: boolean } | undefined>(undefined);

  // Wait for both estimate data (if editing) and customers to load before rendering the form
  const isFormReady = isDataLoaded && !isCustomersLoading;

  React.useEffect(() => {
    // Fetch company vat status ONCE from profile/company.
    // If company is loaded, update VAT status for child fields
    if (profile?.company_id) {
      // Fetch company info for current session
      (async () => {
        const result = await supabase
          .from('companies')
          .select('is_vat_registered')
          .eq('id', profile.company_id)
          .single();
        setCompanyVatStatus({ is_vat_registered: !!result.data?.is_vat_registered });
      })();
    }
  }, [profile?.company_id]);

  const onSubmit = (data: EstimateFormValues) => {
    console.log('[EstimateForm] Form submitted with data:', data);
    handleSubmit(data, (createdEstimateId: string) => {
      console.log('[EstimateForm] handleSubmit success callback called with ID:', createdEstimateId);
      const targetPath = `/estimates/edit/${createdEstimateId}/items`;
      console.log('[EstimateForm] Navigating to:', targetPath);
      try {
        navigate(targetPath);
        console.log('[EstimateForm] Navigation completed successfully');
      } catch (error) {
        console.error('[EstimateForm] Navigation error:', error);
      }
    });
  };

  if (!isFormReady) {
    return (
      <PageLayout maxWidth="lg">
        <div className="space-y-6">
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" asChild>
              <Link to="/estimates">
                <ArrowLeft className="h-4 w-4 mr-1" />
                Back to Estimates
              </Link>
            </Button>
          </div>
          <div>
            <h1 className="text-2xl font-bold font-montserrat">
              {isEditMode ? 'Edit' : 'Create'} Estimate
            </h1>
            <p className="text-sm text-muted-foreground">Loading...</p>
          </div>
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout maxWidth="lg">
      <div className="space-y-6">
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" asChild>
            <Link to="/estimates">
              <ArrowLeft className="h-4 w-4 mr-1" />
              Back to Estimates
            </Link>
          </Button>
        </div>

        <div>
          <h1 className="text-2xl font-bold font-montserrat">
            {isEditMode ? 'Edit' : 'Create'} Estimate
          </h1>
          <p className="text-sm text-muted-foreground">
            {isEditMode
              ? 'Update the details of your estimate'
              : 'Create a new estimate with all the necessary details'}
          </p>
        </div>

        {/* Progress Indicator */}
        <EstimateProgress
          currentStep="details"
          completedSteps={isEditMode ? [] : []}
        />

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <EstimateBasicFields form={form} customers={customers} />
            <EstimateSettingsFields form={form} companyVatStatus={companyVatStatus} />
            <EstimateAdditionalFields form={form} />
            <EstimateFormActions isLoading={isLoading} isEditMode={isEditMode} />
          </form>
        </Form>
      </div>
    </PageLayout>
  );
};

export default EstimateForm;
